import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import {
  IMAGE_MAX_AUTO_RETRIES,
  IMAGE_RETRY_DELAYS_MS,
  ImageZoomModal,
  withRetryParam,
  Button,
} from '../../../shared/ui';
import { IMacCardInfo } from '../../../store/mac.slice';

export const makeMacCardUrl = (url: string) => {
  if (!url) return '';

  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}/${url}`;
};

export interface IMacCardProps extends Omit<IMacCardInfo, 'selected'> {
  onZoomToggle?: (zoom: boolean) => void;
  /** false — карта не реагирует на клик (зум делает родитель, например полноэкранный просмотр) */
  zoomable?: boolean;
  /** Картинка заполняет контейнер целиком (object-cover), скругление и обрезку делает контейнер-рамка */
  fill?: boolean;
}

export const MacCard: React.FC<IMacCardProps> = ({ id, img, onZoomToggle, zoomable = true, fill = false }) => {
  const [loaded, setLoaded] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!failed) return;
    if (attempt >= IMAGE_MAX_AUTO_RETRIES) return;

    const timer = setTimeout(() => {
      setFailed(false);
      setAttempt((prev) => prev + 1);
    }, IMAGE_RETRY_DELAYS_MS[attempt] ?? 1500);

    return () => clearTimeout(timer);
  }, [failed, attempt]);

  const gaveUp = failed && attempt >= IMAGE_MAX_AUTO_RETRIES;

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);

    return () => clearTimeout(timer);
  }, [zoomed]);

  const src = makeMacCardUrl(img);

  return (
    <div
      key={id}
      className={classNames('relative w-full h-full flex overflow-hidden justify-center ', {
        'flex-col': zoomed,
        'duration-800': visible && zoomed,
        'duration-200': visible && !zoomed,
        'opacity-100 transition-opacity': visible,
        'opacity-0': !visible,
      })} // Добавляем transition
    >
      {!loaded && !gaveUp && (
        <div
          data-testid="mac-card-placeholder"
          className={classNames('absolute inset-0 z-10 animate-pulse bg-white/10', !fill && 'rounded-card')}
          aria-hidden
        />
      )}
      {gaveUp && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 px-2 text-center text-ui-xs text-white/80">
          <span>Не удалось загрузить карту</span>
          <Button
            size="sm"
            variant="ghostInverse"
            onClick={(e) => {
              e.stopPropagation();
              setFailed(false);
              setAttempt((prev) => prev + 1);
            }}
          >
            Повторить
          </Button>
        </div>
      )}

      <div
        className={classNames('w-full h-full contents overflow-hidden', {
          'cursor-zoom-in': zoomable && (!zoomed || !onZoomToggle),
          'cursor-zoom-out': zoomable && zoomed && onZoomToggle,
        })}
        onClick={
          zoomable
            ? () => {
                setZoomed((z) => {
                  onZoomToggle && onZoomToggle(!z);

                  return !z;
                });
                setVisible(false);
              }
            : undefined
        }
      >
        <img
          src={withRetryParam(src, attempt)}
          className={classNames(fill ? 'h-full w-full object-cover' : 'object-contain rounded-card h-full ', {
            'opacity-0': !loaded,
            'opacity-100': loaded,
          })}
          alt={`mac-${id}`}
          onLoad={() => {
            setLoaded(true);
            setFailed(false);
          }}
          onError={() => setFailed(true)}
        />
      </div>
      {zoomed && !onZoomToggle && <ImageZoomModal src={src} onClose={() => setZoomed(false)} />}
      {zoomed && <div className="grow-1"></div>}
    </div>
  );
};
